/**
 * Custom pet portrait (wall art) — renderer.
 *
 * Draws the portrait design on a canvas: background colour (with a light
 * paper grain), the pet painting(s) and the name. Everything is laid out
 * in "design units": the design is always DESIGN_WIDTH wide and as tall
 * as the chosen print size's shape (8x10 -> 1500, 12x16 -> 1600), so a
 * customer's moves/resizes mean the same thing at every output size —
 * the on-screen preview, the wall mockup and the full-size print file.
 *
 * Each pet has an adjust { scale, offsetX, offsetY, rotation } on top of
 * a default layout (pets side by side, chests running off the bottom
 * edge); the name has { scale, offsetX, offsetY, color, font } on top of
 * a default spot centred near the top.
 *
 * Also draws the "on the wall" mockup (poster, framed print or canvas)
 * used for the preview switch, the product gallery and the cart image.
 *
 * Exposed as window.NavaBirdWallArt (plain script, like the builder).
 */
(function () {
  const DESIGN_WIDTH = 1200;

  // Name fonts. Files are the theme's bundled fonts (see the embed's
  // render-assets JSON); registered under their own family names.
  const FONTS = {
    calligraphy: { label: 'Calligraphy', family: 'NBW Calligraphy', size: 150, upper: false, spacing: 0, urlKey: 'fontCalligraphyUrl' },
    script: { label: 'Script', family: 'NBW Script', size: 165, upper: false, spacing: 0, urlKey: 'fontScriptUrl' },
    elegant: { label: 'Elegant', family: 'NBW Thin', size: 84, upper: true, spacing: 0.42, urlKey: 'fontThinUrl' },
    modern: { label: 'Modern', family: 'NBW Modern', size: 92, upper: true, spacing: 0.12, urlKey: 'fontModernUrl' },
    // House portrait (loaded only where the page passes their URLs):
    // Dancing Script (medium) for line 1, Xanh Mono typewriter for line 2.
    dancing: { label: 'Dancing', family: 'NBW Dancing', size: 156, upper: false, spacing: 0, urlKey: 'fontDancingUrl' },
    typewriter: { label: 'Typewriter', family: 'NBW Typewriter', size: 112, upper: false, spacing: 0, urlKey: 'fontTypewriterUrl' },
  };

  const DEFAULT_TEXT_ADJUST = { scale: 1, offsetX: 0, offsetY: 0, color: '#2b2522', font: 'calligraphy' };
  const DEFAULT_PET_ADJUST = { scale: 1, offsetX: 0, offsetY: 0, rotation: 0 };

  // Where the name's centre sits by default, as a share of the height:
  // high up, with calm space between it and the pet.
  const PET_TEXT_CENTER_Y = 0.14;
  let TEXT_CENTER_Y = PET_TEXT_CENTER_Y;

  // Layout of the painting(s): 'pet' (default: pets side by side on the
  // bottom edge, name on top) or 'house' (one wide painting centred a
  // little above the middle, text underneath). Set once per page with
  // NavaBirdWallArt.configure({ layout: 'house' }).
  let LAYOUT = 'pet';
  // House defaults chosen by the store owner on a 12x16 (2026-11-02, with the
  // ?layout=1 box): painting 85.8% wide centred at 43.9% down; text centred
  // at 86.7% down at 74% of the fonts' normal size.
  const HOUSE = { maxW: 0.92, maxH: 0.767, centerY: 0.439, textY: 0.867, textSize: 0.74 };
  let TEXT_SIZE_FACTOR = 1;
  // Font for the second text line, whatever line 1 uses (house: 'typewriter').
  let SUB_FONT_FIXED = null;
  function configure({ layout, subFont } = {}) {
    SUB_FONT_FIXED = subFont && FONTS[subFont] ? subFont : null;
    LAYOUT = layout === 'house' ? 'house' : 'pet';
    TEXT_CENTER_Y = LAYOUT === 'house' ? HOUSE.textY : PET_TEXT_CENTER_Y;
    TEXT_SIZE_FACTOR = LAYOUT === 'house' ? HOUSE.textSize : 1;
  }

  const TEXT_MAX_WIDTH = 0.92; // share of the width a long name may use

  function designHeight(ratio) {
    return Math.round(DESIGN_WIDTH * ratio);
  }

  function defaultPetAdjusts(count) {
    return Array.from({ length: Math.max(1, count) }, () => ({ ...DEFAULT_PET_ADJUST }));
  }

  let fontsPromise = null;
  function loadFonts(urls = {}) {
    if (fontsPromise) return fontsPromise;
    if (!window.FontFace || !document.fonts) {
      fontsPromise = Promise.resolve();
      return fontsPromise;
    }
    fontsPromise = Promise.all(
      Object.values(FONTS).map((f) => {
        const url = urls[f.urlKey];
        if (!url) return null;
        return new FontFace(f.family, `url(${url})`).load().then(
          (face) => document.fonts.add(face),
          (err) => console.error(`Portrait font failed to load (${f.family})`, err)
        );
      })
    );
    return fontsPromise;
  }

  /**
   * Returns a copy of a pet painting with contrast/colour adjusted (done
   * once per painting, not per frame). Options (all optional):
   *   contrast   - 1 = unchanged, 1.15 = 15% more contrast (around mid-grey)
   *   sCurve     - 0..1, extra midtone punch that keeps shadow/highlight detail
   *   saturation - 1 = unchanged
   * Transparency is kept as it is.
   */
  function enhancePet(image, { contrast = 1, sCurve = 0, saturation = 1 } = {}) {
    const w = image.naturalWidth || image.width;
    const h = image.naturalHeight || image.height;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(image, 0, 0);
    if (contrast === 1 && !sCurve && saturation === 1) return c;
    const lut = new Uint8ClampedArray(256);
    for (let v = 0; v < 256; v++) {
      let x = v / 255;
      x = (x - 0.5) * contrast + 0.5;
      if (sCurve) {
        const s = 0.5 - 0.5 * Math.cos(Math.PI * Math.min(1, Math.max(0, x)));
        x = x + (s - x) * sCurve;
      }
      lut[v] = Math.round(x * 255);
    }
    const data = ctx.getImageData(0, 0, w, h);
    const d = data.data;
    for (let i = 0; i < d.length; i += 4) {
      if (!d[i + 3]) continue;
      let r = lut[d[i]];
      let g = lut[d[i + 1]];
      let b = lut[d[i + 2]];
      if (saturation !== 1) {
        const grey = 0.299 * r + 0.587 * g + 0.114 * b;
        r = grey + (r - grey) * saturation;
        g = grey + (g - grey) * saturation;
        b = grey + (b - grey) * saturation;
      }
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
    }
    ctx.putImageData(data, 0, 0);
    return c;
  }

  // Default spots for the pets (design units): side by side at the same
  // height, slightly overlapping, sitting on the bottom edge.
  function basePetRects(pets, height, hasText = true) {
    if (LAYOUT === 'house') {
      // No text at all: the painting sits in the middle of the print.
      const centerY = hasText ? HOUSE.centerY : 0.5;
      return pets.map((p) => {
        const a = p.bounds.w / p.bounds.h;
        const h = Math.min(height * HOUSE.maxH, (DESIGN_WIDTH * HOUSE.maxW) / a);
        const w = a * h;
        return { x: (DESIGN_WIDTH - w) / 2, y: height * centerY - h / 2, w, h };
      });
    }
    const OVERLAP = 0.06;
    const many = pets.length > 1;
    const maxW = DESIGN_WIDTH * (many ? 0.98 : 0.94);
    const maxH = height * (many ? 0.62 : 0.8);
    // The chest runs a little past the bottom edge, like a painted
    // portrait cropped at the bottom (so its painted end never shows).
    const BLEED = 0.08;
    const aspects = pets.map((p) => p.bounds.w / p.bounds.h);
    const rowAt = (h) => aspects.reduce((sum, a, i) => sum + a * h * (i === 0 ? 1 : 1 - OVERLAP), 0);
    const h = Math.min(maxH, maxH * (maxW / rowAt(maxH)));
    let x = (DESIGN_WIDTH - rowAt(h)) / 2;
    return aspects.map((a, i) => {
      const w = a * h;
      if (i > 0) x -= w * OVERLAP;
      const rect = { x, y: height - h * (1 - BLEED), w, h };
      x += w;
      return rect;
    });
  }

  // A pet's adjust scales it around its bottom centre (so a pet sitting
  // on the bottom edge stays there) and shifts it by its offsets.
  function petRects(pets, height, hasText = true) {
    const base = basePetRects(pets, height, hasText);
    return pets.map((pet, i) => {
      const b = base[i];
      const a = pet.adjust || DEFAULT_PET_ADJUST;
      const w = b.w * a.scale;
      const h = b.h * a.scale;
      const cx = b.x + b.w / 2 + a.offsetX;
      if (LAYOUT === 'house') {
        // The house painting zooms around its centre.
        const cy = b.y + b.h / 2 + a.offsetY;
        return { x: cx - w / 2, y: cy - h / 2, w, h };
      }
      const bottom = b.y + b.h + a.offsetY;
      return { x: cx - w / 2, y: bottom - h, w, h };
    });
  }

  // Second text line (house portrait: "Home Sweet Home" over "4713 Clark
  // Street"): smaller, in a calm companion font, centred under the first.
  const SUB_FONT = { calligraphy: 'elegant', script: 'elegant', elegant: 'elegant', modern: 'modern' };
  const SUB_SCALE = 0.5; // line 2 size, share of its font's normal size
  const LINE_GAP = 0.2; // space between the lines, share of line 1's size

  // One line of text in one font: measures its ink and draws it.
  function textLine(ctx, str, f, wantedSize) {
    const display = f.upper ? str.toUpperCase() : str;
    // Drawn as one run when there's no extra letter spacing (keeps kerning and
    // the joins between letters in script fonts); spaced capitals go letter
    // by letter.
    const runs = f.spacing ? [...display] : [display];

    // Measures the ink — the letters as actually drawn, including swashes
    // and tails that reach outside the normal letter spacing — so the
    // selection box and drag area sit evenly around the visible name.
    const measureAt = (size) => {
      ctx.font = `${size}px '${f.family}', sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      const offsets = [];
      let pen = 0;
      let left = Infinity;
      let right = -Infinity;
      let ascent = 0;
      let descent = 0;
      runs.forEach((run, i) => {
        const m = ctx.measureText(run);
        offsets.push(pen);
        left = Math.min(left, pen - (m.actualBoundingBoxLeft || 0));
        right = Math.max(right, pen + (m.actualBoundingBoxRight == null ? m.width : m.actualBoundingBoxRight));
        ascent = Math.max(ascent, m.actualBoundingBoxAscent || size * 0.72);
        descent = Math.max(descent, m.actualBoundingBoxDescent || size * 0.2);
        pen += m.width + (i < runs.length - 1 ? f.spacing * size : 0);
      });
      return { offsets, left, ascent, descent, w: right - left, h: ascent + descent };
    };

    let size = wantedSize;
    let ink = measureAt(size);
    const maxW = DESIGN_WIDTH * TEXT_MAX_WIDTH;
    if (ink.w > maxW) {
      size *= maxW / ink.w;
      ink = measureAt(size);
    }
    return {
      size,
      w: ink.w,
      h: ink.h,
      // Draws the line with its ink box's top-left at (x, y).
      draw(target, x, y, color) {
        const startX = x - ink.left; // pen position that puts the ink at x
        const baseline = y + ink.ascent;
        target.save();
        target.font = `${size}px '${f.family}', sans-serif`;
        target.fillStyle = color;
        target.textBaseline = 'alphabetic';
        target.textAlign = 'left';
        runs.forEach((run, i) => target.fillText(run, startX + ink.offsets[i], baseline));
        target.restore();
      },
    };
  }

  // Measures the name (in design units) and returns its box plus a draw()
  // for it, or null when there's no name. A name with a line break ("line
  // 1\nline 2") gets a smaller second line; both move and resize together.
  function nameLayout(ctx, name, text, height) {
    const f = FONTS[text.font] || FONTS[DEFAULT_TEXT_ADJUST.font];
    const [first = '', second = ''] = String(name || '').split('\n').map((l) => l.trim());
    if (!first && !second) return null;
    const lines = [];
    if (first) lines.push(textLine(ctx, first, f, f.size * text.scale * TEXT_SIZE_FACTOR));
    if (second) {
      const f2 = FONTS[SUB_FONT_FIXED || SUB_FONT[text.font] || 'elegant'];
      lines.push(textLine(ctx, second, f2, f2.size * SUB_SCALE * text.scale * TEXT_SIZE_FACTOR));
    }
    const gap = lines.length > 1 ? lines[0].size * LINE_GAP : 0;
    const w = Math.max(...lines.map((l) => l.w));
    const h = lines.reduce((sum, l) => sum + l.h, 0) + gap;

    const cx = DESIGN_WIDTH / 2 + text.offsetX;
    const cy = height * TEXT_CENTER_Y + text.offsetY;
    const rect = { x: cx - w / 2, y: cy - h / 2, w, h };

    return {
      rect,
      draw(target) {
        let y = rect.y;
        lines.forEach((l) => {
          l.draw(target, cx - l.w / 2, y, text.color);
          y += l.h + gap;
        });
      },
    };
  }

  function drawBackground(ctx, height, background, paperImage, pad = 0) {
    ctx.fillStyle = background;
    ctx.fillRect(-pad, -pad, DESIGN_WIDTH + 2 * pad, height + 2 * pad);
    if (paperImage) {
      // Light paper grain on any colour (the paper scan is near-white, so
      // multiply keeps the colour and adds only the texture).
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = 0.9;
      ctx.drawImage(paperImage, -pad, -pad, DESIGN_WIDTH + 2 * pad, height + 2 * pad);
      ctx.restore();
    }
  }

  function drawPet(ctx, pet, rect) {
    const b = pet.bounds;
    const rotation = ((pet.adjust && pet.adjust.rotation) || 0) * (Math.PI / 180);
    ctx.save();
    ctx.translate(rect.x + rect.w / 2, rect.y + rect.h / 2);
    if (rotation) ctx.rotate(rotation);
    ctx.drawImage(pet.image, b.x, b.y, b.w, b.h, -rect.w / 2, -rect.h / 2, rect.w, rect.h);
    ctx.restore();
  }

  /**
   * @param {object} opts
   * @param {number} opts.ratio - print height / width (e.g. 16/12)
   * @param {string} opts.background - CSS colour
   * @param {HTMLImageElement} [opts.paperImage]
   * @param {Array<{image, bounds, adjust}>} opts.pets
   * @param {string} opts.name
   * @param {object} opts.text - { scale, offsetX, offsetY, color, font }
   * @param {number} [opts.outputWidth] - canvas width in px (default DESIGN_WIDTH)
   * @returns {{ canvas, petRects, textRect, height }} rects in design units
   */
  // `pad` (design units, optional): extra area on every side for print
  // bleed / canvas wrap; the background continues into it and the pets
  // carry on where their painting does. outputWidth is the width WITHOUT pad.
  function renderDesign({ ratio, background, paperImage, pets, name, text, outputWidth = DESIGN_WIDTH, pad = 0 }) {
    const height = designHeight(ratio);
    const k = outputWidth / DESIGN_WIDTH;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round((DESIGN_WIDTH + 2 * pad) * k);
    canvas.height = Math.round((height + 2 * pad) * k);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.scale(k, k);
    ctx.translate(pad, pad);

    drawBackground(ctx, height, background, paperImage, pad);
    const rects = petRects(pets, height, !!String(name || '').trim());
    pets.forEach((pet, i) => drawPet(ctx, pet, rects[i]));
    const label = nameLayout(ctx, name, text, height);
    if (label) label.draw(ctx);

    return { canvas, petRects: rects, textRect: label ? label.rect : null, height };
  }

  // --- "On the wall" mockup -------------------------------------------

  function drawWall(ctx, size) {
    const wall = ctx.createLinearGradient(0, 0, 0, size);
    wall.addColorStop(0, '#efe9e0');
    wall.addColorStop(1, '#e2d9cc');
    ctx.fillStyle = wall;
    ctx.fillRect(0, 0, size, size);
    // Soft daylight from the top left
    const light = ctx.createRadialGradient(size * 0.2, size * 0.05, 0, size * 0.2, size * 0.05, size * 0.9);
    light.addColorStop(0, 'rgba(255, 255, 255, 0.55)');
    light.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = light;
    ctx.fillRect(0, 0, size, size);
  }

  /**
   * @param {object} opts
   * @param {HTMLCanvasElement} opts.design - output of renderDesign
   * @param {string} opts.format - 'digital' | 'poster' | 'framed' | 'canvas'
   * @param {number} [opts.size] - square mockup size in px
   * @returns {{ canvas, designRect }} designRect in mockup px
   */
  function renderWallMockup({ design, format, size = 1200 }) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    drawWall(ctx, size);

    const ratio = design.height / design.width;
    // Frame colours: 'framed' (light oak, the original) or the builder's
    // format keys black / white / wood.
    const FRAME_COLORS = {
      framed: ['#dcbb92', '#cfa878', '#bf9465'],
      wood: ['#dcbb92', '#cfa878', '#bf9465'],
      black: ['#3a3a3a', '#1f1f1f', '#111111'],
      white: ['#ffffff', '#f2f2f0', '#e3e3e0'],
    };
    const frameColors = FRAME_COLORS[format];
    const framed = !!frameColors;
    const frameShare = framed ? 0.055 : 0; // frame width, share of the design width
    let dh = size * 0.68;
    let dw = dh / ratio;
    const maxW = size * 0.62;
    if (dw * (1 + frameShare * 2) > maxW) {
      dw = maxW / (1 + frameShare * 2);
      dh = dw * ratio;
    }
    const t = dw * frameShare;
    const cx = size / 2;
    const cy = size * 0.47;
    const designRect = { x: cx - dw / 2, y: cy - dh / 2, w: dw, h: dh };
    const outer = { x: designRect.x - t, y: designRect.y - t, w: dw + t * 2, h: dh + t * 2 };

    // Shadow on the wall
    ctx.save();
    ctx.shadowColor = 'rgba(40, 28, 18, 0.32)';
    ctx.shadowBlur = size * 0.035;
    ctx.shadowOffsetX = size * 0.006;
    ctx.shadowOffsetY = size * 0.016;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(outer.x, outer.y, outer.w, outer.h);
    ctx.restore();

    if (framed) {
      // Light oak frame with a little bevel
      const wood = ctx.createLinearGradient(outer.x, outer.y, outer.x + outer.w, outer.y + outer.h);
      wood.addColorStop(0, frameColors[0]);
      wood.addColorStop(0.5, frameColors[1]);
      wood.addColorStop(1, frameColors[2]);
      ctx.fillStyle = wood;
      ctx.fillRect(outer.x, outer.y, outer.w, outer.h);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = Math.max(1, t * 0.08);
      ctx.strokeRect(outer.x + t * 0.06, outer.y + t * 0.06, outer.w - t * 0.12, outer.h - t * 0.12);
      ctx.strokeStyle = 'rgba(90, 60, 30, 0.45)';
      ctx.strokeRect(designRect.x - 1, designRect.y - 1, dw + 2, dh + 2);
    }

    ctx.drawImage(design, designRect.x, designRect.y, dw, dh);

    if (framed) {
      // The frame casts a thin shadow onto the print
      const inner = ctx.createLinearGradient(0, designRect.y, 0, designRect.y + t * 0.6);
      inner.addColorStop(0, 'rgba(0, 0, 0, 0.18)');
      inner.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = inner;
      ctx.fillRect(designRect.x, designRect.y, dw, t * 0.6);
    } else if (format === 'canvas') {
      // Wrapped edge on the right and bottom (canvas depth)
      const depth = dw * 0.03;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx.beginPath();
      ctx.moveTo(designRect.x + dw, designRect.y);
      ctx.lineTo(designRect.x + dw + depth, designRect.y + depth);
      ctx.lineTo(designRect.x + dw + depth, designRect.y + dh + depth);
      ctx.lineTo(designRect.x + dw, designRect.y + dh);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.beginPath();
      ctx.moveTo(designRect.x, designRect.y + dh);
      ctx.lineTo(designRect.x + depth, designRect.y + dh + depth);
      ctx.lineTo(designRect.x + dw + depth, designRect.y + dh + depth);
      ctx.lineTo(designRect.x + dw, designRect.y + dh);
      ctx.closePath();
      ctx.fill();
    }

    return { canvas, designRect };
  }

  // --- Photo mockup (a real room photo from a PSD mockup) ----------------
  //
  // The PSD is split once into: the room with the empty frame (`room`), the
  // artwork window from its smart object ([x, y, w, h] in room px), and the
  // brightness boost the PSD applies on top. The design is drawn into the
  // window with "multiply", like the PSD's smart object, so the paper, light
  // and the frame's shadow show through the customer's print.

  const brightenLuts = {};
  function brightenLut(amount) {
    if (!brightenLuts[amount]) {
      const lut = new Uint8ClampedArray(256);
      for (let v = 0; v < 256; v++) lut[v] = v + amount * 1.6 * (1 - v / 255);
      brightenLuts[amount] = lut;
    }
    return brightenLuts[amount];
  }

  /**
   * @param {object} opts
   * @param {HTMLCanvasElement} opts.design - renderDesign output, drawn at the window's shape
   * @param {HTMLImageElement} opts.room - room photo with the empty frame/canvas
   * @param {number[]} opts.window - [x, y, w, h] of the artwork in room px
   * @param {string} [opts.blend] - how the art sits on the room, like the
   *   PSD's smart object: 'multiply' (frames) or 'linear-burn' (canvas)
   * @param {number} [opts.brighten] - a PSD brightness boost, if it has one
   * @param {object} [opts.overlay] - a PSD layer that goes over the art:
   *   { image, x, y, blend: 'screen', opacity }
   * @returns {{ canvas, designRect }}
   */
  function renderPhotoMockup({ design, room, window: win, blend = 'multiply', brighten = 0, overlay = null }) {
    const [x, y, w, h] = win;
    const canvas = document.createElement('canvas');
    canvas.width = room.naturalWidth || room.width;
    canvas.height = room.naturalHeight || room.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(room, 0, 0);

    const art = document.createElement('canvas');
    art.width = w;
    art.height = h;
    const a = art.getContext('2d');
    a.imageSmoothingQuality = 'high';
    a.drawImage(design, 0, 0, w, h);
    const artData = a.getImageData(0, 0, w, h);
    const ad = artData.data;
    if (brighten) {
      const lut = brightenLut(brighten);
      for (let i = 0; i < ad.length; i += 4) {
        ad[i] = lut[ad[i]];
        ad[i + 1] = lut[ad[i + 1]];
        ad[i + 2] = lut[ad[i + 2]];
      }
    }

    if (blend === 'linear-burn') {
      // Canvas has no built-in linear burn: room + art - 255, per channel.
      const roomData = ctx.getImageData(x, y, w, h);
      const rd = roomData.data;
      for (let i = 0; i < rd.length; i += 4) {
        rd[i] = rd[i] + ad[i] - 255;
        rd[i + 1] = rd[i + 1] + ad[i + 1] - 255;
        rd[i + 2] = rd[i + 2] + ad[i + 2] - 255;
      }
      ctx.putImageData(roomData, x, y);
    } else {
      a.putImageData(artData, 0, 0);
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.drawImage(art, x, y);
      ctx.restore();
    }

    if (overlay && overlay.image) {
      ctx.save();
      ctx.globalCompositeOperation = overlay.blend || 'screen';
      ctx.globalAlpha = overlay.opacity == null ? 1 : overlay.opacity;
      ctx.drawImage(overlay.image, overlay.x || 0, overlay.y || 0);
      ctx.restore();
    }
    return { canvas, designRect: { x, y, w, h } };
  }

  // --- Live scenes (angled poster/frame photos from PSD mockups) --------
  //
  // A scene = room photo (bg) + where the artwork may show (mask) + the
  // artwork's 4 corners (quad, possibly in perspective) + the PSD's layers
  // above it (overlays: light, shadow, reflection, frame front) with their
  // blend modes. All pieces are 1080x1080. See scripts' convert_scene.py.

  // 3x3 homography mapping the 4 points `from` onto the 4 points `to`.
  function homography(from, to) {
    const A = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
      const [x, y] = from[i];
      const [u, v] = to[i];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      b.push(v);
    }
    // Gaussian elimination
    for (let c = 0; c < 8; c++) {
      let p = c;
      for (let r = c + 1; r < 8; r++) if (Math.abs(A[r][c]) > Math.abs(A[p][c])) p = r;
      [A[c], A[p]] = [A[p], A[c]];
      [b[c], b[p]] = [b[p], b[c]];
      for (let r = 0; r < 8; r++) {
        if (r === c) continue;
        const f = A[r][c] / A[c][c];
        for (let k2 = c; k2 < 8; k2++) A[r][k2] -= f * A[c][k2];
        b[r] -= f * b[c];
      }
    }
    return b.map((v, i) => v / A[i][i]); // h0..h7 (h8 = 1)
  }

  function blendChannel(mode, d, s) {
    switch (mode) {
      case 'multiply':
        return (d * s) / 255;
      case 'linear-burn':
        return d + s - 255;
      case 'screen':
        return 255 - ((255 - d) * (255 - s)) / 255;
      case 'hard-light':
        return s <= 127.5 ? (2 * d * s) / 255 : 255 - (2 * (255 - d) * (255 - s)) / 255;
      default:
        return s;
    }
  }

  /**
   * @param {object} opts
   * @param {HTMLCanvasElement} opts.design - renderDesign output at the scene's ratio
   * @param {object} opts.scene - { quad, blend, brighten, overlays: [{ x, y, blend }] }
   * @param {object} opts.images - { bg, mask, overlays: [Image] } (loaded)
   * @returns {HTMLCanvasElement} 1080x1080
   */
  // `pad` (px, optional): the design canvas has this much extra around the
  // print (background continued); scene.quad is the FRONT face, and pixels
  // just outside it (a canvas's wrapped sides) show that extra area.
  function renderScene({ design, scene, images, pad = 0 }) {
    const S = 1080;
    const canvas = document.createElement('canvas');
    canvas.width = S;
    canvas.height = S;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(images.bg, 0, 0, S, S);

    // Mask (where the artwork may show)
    const mc = document.createElement('canvas');
    mc.width = S;
    mc.height = S;
    const mctx = mc.getContext('2d', { willReadFrequently: true });
    mctx.drawImage(images.mask, 0, 0, S, S);
    const mask = mctx.getImageData(0, 0, S, S).data;

    // Artwork pixels
    const dw = design.width;
    const dh = design.height;
    const dctx = design.getContext('2d', { willReadFrequently: true });
    const src = dctx.getImageData(0, 0, dw, dh).data;
    const tw = dw - 2 * pad;
    const th = dh - 2 * pad;
    const h = homography(scene.quad, [[pad, pad], [pad + tw, pad], [pad + tw, pad + th], [pad, pad + th]]);
    const area = scene.wrapBox || null; // [x0, y0, x1, y1] covering the sides too
    const xs = scene.quad.map((q) => q[0]);
    const ys = scene.quad.map((q) => q[1]);
    const x0 = Math.max(0, Math.floor(area && pad ? area[0] : Math.min(...xs)));
    const x1 = Math.min(S, Math.ceil(area && pad ? area[2] : Math.max(...xs)));
    const y0 = Math.max(0, Math.floor(area && pad ? area[1] : Math.min(...ys)));
    const y1 = Math.min(S, Math.ceil(area && pad ? area[3] : Math.max(...ys)));
    const region = ctx.getImageData(x0, y0, x1 - x0, y1 - y0);
    const out = region.data;
    const rw = x1 - x0;
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const m = mask[(y * S + x) * 4] / 255;
        if (m <= 0) continue;
        const px = x + 0.5;
        const py = y + 0.5;
        const den = h[6] * px + h[7] * py + 1;
        const u = (h[0] * px + h[1] * py + h[2]) / den - 0.5;
        const v = (h[3] * px + h[4] * py + h[5]) / den - 0.5;
        if (u < -0.5 || v < -0.5 || u > dw - 0.5 || v > dh - 0.5) continue;
        const ux = Math.min(dw - 1, Math.max(0, u));
        const vy = Math.min(dh - 1, Math.max(0, v));
        const ix = Math.floor(ux);
        const iy = Math.floor(vy);
        const fx = ux - ix;
        const fy = vy - iy;
        const ix1 = Math.min(dw - 1, ix + 1);
        const iy1 = Math.min(dh - 1, iy + 1);
        const o = ((y - y0) * rw + (x - x0)) * 4;
        for (let c = 0; c < 3; c++) {
          const s00 = src[(iy * dw + ix) * 4 + c];
          const s10 = src[(iy * dw + ix1) * 4 + c];
          const s01 = src[(iy1 * dw + ix) * 4 + c];
          const s11 = src[(iy1 * dw + ix1) * 4 + c];
          const sv = (s00 * (1 - fx) + s10 * fx) * (1 - fy) + (s01 * (1 - fx) + s11 * fx) * fy;
          const d = out[o + c];
          out[o + c] = d + (blendChannel(scene.blend, d, sv) - d) * m;
        }
      }
    }
    ctx.putImageData(region, x0, y0);

    // Layers above the artwork. A PSD brightness step applies after the
    // first `brightenAt` overlays (so a layer placed above it, like a
    // recoloured frame photo, isn't brightened twice).
    const overlays = scene.overlays || [];
    const brightenAt = scene.brightenAt == null ? overlays.length : scene.brightenAt;
    const drawOverlay = (ov, i) => {
      const img = images.overlays[i];
      if (!img) return;
      const w = img.naturalWidth || img.width;
      const hh = img.naturalHeight || img.height;
      if (ov.blend === 'linear-burn') {
        const oc = document.createElement('canvas');
        oc.width = w;
        oc.height = hh;
        const octx = oc.getContext('2d', { willReadFrequently: true });
        octx.drawImage(img, 0, 0);
        const od = octx.getImageData(0, 0, w, hh).data;
        const reg = ctx.getImageData(ov.x, ov.y, w, hh);
        const rd = reg.data;
        for (let j = 0; j < rd.length; j += 4) {
          const a = od[j + 3] / 255;
          if (!a) continue;
          for (let c = 0; c < 3; c++) {
            const d = rd[j + c];
            rd[j + c] = d + (d + od[j + c] - 255 - d) * a;
          }
        }
        ctx.putImageData(reg, ov.x, ov.y);
      } else {
        ctx.save();
        ctx.globalCompositeOperation = ov.blend === 'normal' ? 'source-over' : ov.blend;
        ctx.drawImage(img, ov.x, ov.y);
        ctx.restore();
      }
    };
    const applyBrighten = () => {
      if (!scene.brighten) return;
      const lut = brightenLut(scene.brighten);
      const all = ctx.getImageData(0, 0, S, S);
      const ad = all.data;
      for (let j = 0; j < ad.length; j += 4) {
        ad[j] = lut[ad[j]];
        ad[j + 1] = lut[ad[j + 1]];
        ad[j + 2] = lut[ad[j + 2]];
      }
      ctx.putImageData(all, 0, 0);
    };
    overlays.forEach((ov, i) => {
      if (i === brightenAt) applyBrighten();
      drawOverlay(ov, i);
    });
    if (brightenAt >= overlays.length) applyBrighten();
    return canvas;
  }

  window.NavaBirdWallArt = {
    DESIGN_WIDTH,
    FONTS,
    DEFAULT_TEXT_ADJUST,
    DEFAULT_PET_ADJUST,
    designHeight,
    defaultPetAdjusts,
    loadFonts,
    renderDesign,
    renderWallMockup,
    renderPhotoMockup,
    renderScene,
    enhancePet,
    configure,
  };
})();
